import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Sidebar, MobileHeader } from '../components/shared/Sidebar';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import axios from '../lib/axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import html2canvas from 'html2canvas';
import {
  TrendingUp, TrendingDown, Users, DollarSign, BarChart3,
  Trophy, Crown, ChevronRight, X, Calendar, ArrowUpRight, ArrowDownRight,
  Download, Copy, Clock, UserPlus, Target, Award, Star, Layers
} from 'lucide-react';
import { formatCurrency } from '../lib/utils';
import { useAutoRefresh } from '../hooks/useAutoRefresh';

const THEME = {
  neonCyan: '#00F5FF',
  neonPurple: '#9945FF',
  neonGold: '#FFD700',
  neonGreen: '#00FF88',
  neonPink: '#FF3366',
  neonOrange: '#FF6B00',
};

const GRADE_COLORS = {
  'A+': '#FFD700', 'A': '#00FF88', 'B': '#00F5FF', 'C': '#FF6B00', 'D': '#FF3366'
};

const PERIODS = [
  { key: '24h', label: '24H' },
  { key: '7d', label: '7D' },
  { key: '30d', label: '1M' },
];

function PeriodSelector({ period, setPeriod, customRange, setCustomRange }) {
  const [showCustom, setShowCustom] = useState(false);
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="period-selector">
      {PERIODS.map(p => (
        <button
          key={p.key}
          onClick={() => { setPeriod(p.key); setShowCustom(false); }}
          className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${period === p.key && !showCustom ? 'bg-gradient-to-r from-[#9945FF] to-[#00F5FF] text-black' : 'bg-[#0d0d15] text-gray-400 hover:text-white border border-white/5'}`}
          data-testid={`period-${p.key}`}
        >{p.label}</button>
      ))}
      <button
        onClick={() => setShowCustom(!showCustom)}
        className={`px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-1.5 ${showCustom ? 'bg-[#9945FF] text-white' : 'bg-[#0d0d15] text-gray-400 hover:text-white border border-white/5'}`}
        data-testid="period-custom"
      ><Calendar className="w-3.5 h-3.5" /> Custom</button>
      {showCustom && (
        <div className="flex items-center gap-2">
          <input type="date" value={customRange.start} onChange={e => setCustomRange(r => ({ ...r, start: e.target.value }))}
            className="h-9 px-2 rounded-lg bg-[#0d0d15] border border-white/10 text-white text-xs [color-scheme:dark]" data-testid="custom-start" />
          <span className="text-gray-500 text-xs">to</span>
          <input type="date" value={customRange.end} onChange={e => setCustomRange(r => ({ ...r, end: e.target.value }))}
            className="h-9 px-2 rounded-lg bg-[#0d0d15] border border-white/10 text-white text-xs [color-scheme:dark]" data-testid="custom-end" />
          <Button size="sm" onClick={() => { if (customRange.start && customRange.end) setPeriod('custom'); }}
            className="h-9 bg-[#00FF88] text-black hover:bg-[#00FF88]/80 text-xs font-bold" data-testid="apply-custom">Apply</Button>
        </div>
      )}
    </div>
  );
}

function StatBox({ label, value, icon: Icon, color, change }) {
  return (
    <div className="p-4 rounded-xl" style={{ background: `${color}10`, border: `1px solid ${color}25` }}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4" style={{ color }} />
        <span className="text-xs text-gray-400 uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-2xl font-black text-white">{value}</p>
      {change !== undefined && change !== 0 && (
        <div className={`flex items-center gap-1 mt-1 text-xs ${change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
          {change >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
          {Math.abs(change)}% vs prev
        </div>
      )}
    </div>
  );
}

function CertBorderSVG() {
  return (
    <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 800 560" fill="none" preserveAspectRatio="none">
      {/* Outer gold frame */}
      <rect x="8" y="8" width="784" height="544" rx="6" stroke="url(#goldGrad)" strokeWidth="3" fill="none" />
      {/* Inner ornamental frame */}
      <rect x="18" y="18" width="764" height="524" rx="4" stroke="url(#goldGrad2)" strokeWidth="1.5" fill="none" strokeDasharray="8 4" />
      {/* Corner ornaments */}
      {[[24,24,1,1],[776,24,-1,1],[24,536,1,-1],[776,536,-1,-1]].map(([cx,cy,sx,sy], i) => (
        <g key={i} transform={`translate(${cx},${cy}) scale(${sx},${sy})`}>
          <path d="M0 0 C0 20 20 20 20 20 L40 20 C30 20 20 10 20 0 Z" fill="url(#goldGrad)" opacity="0.6" />
          <circle cx="6" cy="6" r="3" fill="#FFD700" opacity="0.5" />
        </g>
      ))}
      {/* Decorative side lines */}
      <line x1="60" y1="14" x2="740" y2="14" stroke="#FFD70040" strokeWidth="0.5" />
      <line x1="60" y1="546" x2="740" y2="546" stroke="#FFD70040" strokeWidth="0.5" />
      <line x1="14" y1="60" x2="14" y2="500" stroke="#FFD70040" strokeWidth="0.5" />
      <line x1="786" y1="60" x2="786" y2="500" stroke="#FFD70040" strokeWidth="0.5" />
      <defs>
        <linearGradient id="goldGrad" x1="0" y1="0" x2="800" y2="560">
          <stop offset="0%" stopColor="#C9A84C" />
          <stop offset="30%" stopColor="#FFD700" />
          <stop offset="60%" stopColor="#E8C547" />
          <stop offset="100%" stopColor="#C9A84C" />
        </linearGradient>
        <linearGradient id="goldGrad2" x1="0" y1="0" x2="800" y2="0">
          <stop offset="0%" stopColor="#C9A84C90" />
          <stop offset="50%" stopColor="#FFD70070" />
          <stop offset="100%" stopColor="#C9A84C90" />
        </linearGradient>
      </defs>
    </svg>
  );
}

function CertSeal({ grade, score }) {
  const col = GRADE_COLORS[grade] || '#C9A84C';
  return (
    <div className="relative w-[90px] h-[90px] flex-shrink-0">
      <svg viewBox="0 0 100 100" className="w-full h-full">
        {/* Starburst */}
        {[...Array(16)].map((_, i) => {
          const angle = (i * 22.5) * Math.PI / 180;
          const r1 = 46, r2 = 38;
          const r = i % 2 === 0 ? r1 : r2;
          return <line key={i} x1="50" y1="50" x2={50 + r * Math.cos(angle)} y2={50 + r * Math.sin(angle)} stroke={col} strokeWidth="1.5" opacity="0.3" />;
        })}
        {/* Outer ring */}
        <circle cx="50" cy="50" r="38" fill="none" stroke={col} strokeWidth="2" opacity="0.6" />
        <circle cx="50" cy="50" r="34" fill="none" stroke={col} strokeWidth="0.8" opacity="0.3" />
        {/* Inner filled circle */}
        <circle cx="50" cy="50" r="30" fill={`${col}18`} stroke={col} strokeWidth="1.2" />
        {/* Score arc */}
        <circle cx="50" cy="50" r="26" fill="none" stroke={col} strokeWidth="3" strokeDasharray={`${score * 1.63} 163`}
          strokeLinecap="round" transform="rotate(-90 50 50)" opacity="0.8" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-[22px] font-black leading-none" style={{ color: col }}>{score}</span>
        <span className="text-[8px] font-semibold uppercase tracking-wider" style={{ color: `${col}99` }}>Score</span>
      </div>
    </div>
  );
}

function PerformanceCertificate({ employee, periodLabel, onClose }) {
  const certRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const cur = employee.current;
  const comp = employee.comparison;

  const doExport = async (action) => {
    if (!certRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(certRef.current, {
        backgroundColor: '#0B0E18', scale: 2, useCORS: true, logging: false,
        width: certRef.current.scrollWidth, height: certRef.current.scrollHeight
      });
      if (action === 'download') {
        const link = document.createElement('a');
        link.download = `${employee.employee_name.replace(/\s+/g, '_')}_certificate.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
        toast.success('Certificate downloaded!');
      } else {
        canvas.toBlob(async (blob) => {
          try { await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]); toast.success('Copied!'); }
          catch { toast.error('Copy failed - try download'); }
        });
      }
    } catch { toast.error('Export failed'); }
    setExporting(false);
  };

  const gradeColor = GRADE_COLORS[employee.grade] || '#C9A84C';

  const MetricCell = ({ label, value, color, change, changeSuffix = '%' }) => (
    <div className="text-center py-2">
      <p className="text-[9px] uppercase tracking-widest mb-1" style={{ color: '#8A8A9A' }}>{label}</p>
      <p className="text-[17px] font-black leading-tight" style={{ color: color || '#E8E8F0' }}>{value}</p>
      {change !== undefined && change !== 0 && (
        <span className={`text-[9px] font-semibold ${change >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
          {change >= 0 ? '+' : ''}{change}{changeSuffix}
        </span>
      )}
    </div>
  );

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-3 bg-black/85 backdrop-blur-md" onClick={onClose}>
      <motion.div initial={{ scale: 0.92, y: 30 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, y: 30 }}
        onClick={e => e.stopPropagation()} className="w-full max-w-[860px]">
        
        {/* Export bar */}
        <div className="flex items-center justify-between mb-3 px-1">
          <div className="flex gap-2">
            <Button size="sm" onClick={() => doExport('download')} disabled={exporting}
              className="h-9 bg-gradient-to-r from-amber-600 to-yellow-500 hover:from-amber-500 hover:to-yellow-400 text-black font-bold text-xs border-0" data-testid="cert-download">
              <Download className="w-4 h-4 mr-1.5" /> Download PNG
            </Button>
            <Button size="sm" onClick={() => doExport('copy')} disabled={exporting}
              className="h-9 bg-white/10 hover:bg-white/15 text-white/80 text-xs border border-white/10" data-testid="cert-copy">
              <Copy className="w-4 h-4 mr-1.5" /> Copy Image
            </Button>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors" data-testid="cert-close">
            <X className="w-4 h-4 text-white/60" />
          </button>
        </div>

        {/* ====== CERTIFICATE BODY ====== */}
        <div ref={certRef} className="relative overflow-hidden" style={{
          background: 'linear-gradient(160deg, #0B0E18 0%, #111528 30%, #0D1020 60%, #0B0E18 100%)',
          aspectRatio: '800 / 560',
          padding: '32px',
        }}>
          {/* Ornamental border */}
          <CertBorderSVG />

          {/* Subtle radial glow */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[350px] rounded-full pointer-events-none"
            style={{ background: `radial-gradient(ellipse, ${gradeColor}08 0%, transparent 70%)` }} />

          {/* Content */}
          <div className="relative z-10 h-full flex flex-col" style={{ padding: '12px 20px' }}>
            
            {/* Top section: Title */}
            <div className="text-center mb-1">
              <div className="flex items-center justify-center gap-3 mb-1">
                <div className="h-px flex-1 max-w-[80px]" style={{ background: `linear-gradient(to right, transparent, ${gradeColor}60)` }} />
                <Star className="w-4 h-4" style={{ color: gradeColor, opacity: 0.6 }} />
                <span className="text-[10px] font-semibold uppercase tracking-[5px]" style={{ color: '#8A8A9A' }}>Certificate of</span>
                <Star className="w-4 h-4" style={{ color: gradeColor, opacity: 0.6 }} />
                <div className="h-px flex-1 max-w-[80px]" style={{ background: `linear-gradient(to left, transparent, ${gradeColor}60)` }} />
              </div>
              <h1 className="text-[28px] font-black tracking-wide" style={{
                background: `linear-gradient(135deg, #E8D5A3, #FFD700, #F0C050, #FFD700)`,
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
              }}>PERFORMANCE</h1>
            </div>

            {/* Middle: Name + Seal side by side */}
            <div className="flex items-center justify-center gap-8 my-3">
              {/* Left: Employee Info */}
              <div className="text-center flex-1">
                <p className="text-[10px] uppercase tracking-[3px] mb-1" style={{ color: '#6B6B7B' }}>Awarded to</p>
                <h2 className="text-[26px] font-black text-white leading-tight" style={{ fontFamily: 'Georgia, serif' }}>
                  {employee.employee_name}
                </h2>
                <div className="flex items-center justify-center gap-2 mt-2">
                  <span className="px-3 py-1 rounded-full text-[10px] font-bold border" style={{
                    color: gradeColor, borderColor: `${gradeColor}50`, background: `${gradeColor}10`
                  }}>Grade {employee.grade}</span>
                  <span className="px-3 py-1 rounded-full text-[10px] font-bold text-gray-300 bg-white/5 border border-white/10">
                    Rank #{employee.rank}
                  </span>
                  <span className="px-3 py-1 rounded-full text-[10px] font-bold text-gray-300 bg-white/5 border border-white/10">
                    {employee.page_name}
                  </span>
                </div>
              </div>

              {/* Right: Seal */}
              <CertSeal grade={employee.grade} score={cur.score} />
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3 my-2">
              <div className="flex-1 h-px" style={{ background: `linear-gradient(to right, transparent, #FFD70030, transparent)` }} />
              <Award className="w-4 h-4" style={{ color: '#FFD70060' }} />
              <div className="flex-1 h-px" style={{ background: `linear-gradient(to left, transparent, #FFD70030, transparent)` }} />
            </div>

            {/* Metrics Grid - 4 columns */}
            <div className="grid grid-cols-4 gap-x-1 gap-y-0 flex-1">
              <MetricCell label="Total Deposits" value={formatCurrency(cur.total_deposit)} color="#00FF88" change={comp.deposit_growth} />
              <MetricCell label="Withdrawals" value={formatCurrency(cur.total_withdraw)} color="#FF3366" />
              <MetricCell label="Net Collection" value={formatCurrency(cur.net_collection)} color="#FFD700" change={comp.net_growth} />
              <MetricCell label="Holding %" value={`${cur.holding_pct}%`} color="#00F5FF" />
              <MetricCell label="New Players" value={cur.new_players} color="#9945FF" change={comp.new_players_diff} changeSuffix="" />
              <MetricCell label="Working Hours" value={`${cur.working_hours}h`} color="#E8E8F0" change={comp.hours_diff} changeSuffix="h" />
              <MetricCell label="Activity" value={`${cur.activity_score}/100`} color="#FF6B00" change={comp.activity_diff} changeSuffix="" />
              <MetricCell label="Transactions" value={cur.transactions} color="#00F5FF" />
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between mt-auto pt-2">
              <div className="text-[9px] text-gray-500">{periodLabel}</div>
              <div className="flex items-center gap-2">
                <div className="h-px w-16" style={{ background: `linear-gradient(to right, transparent, #FFD70040)` }} />
                <span className="text-[9px] font-bold tracking-widest" style={{ color: '#C9A84C80' }}>RIDER</span>
                <div className="h-px w-16" style={{ background: `linear-gradient(to left, transparent, #FFD70040)` }} />
              </div>
              <div className="text-[9px] text-gray-500">{new Date().toLocaleDateString()}</div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function PerformanceReportsPage() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('30d');
  const [customRange, setCustomRange] = useState({ start: '', end: '' });
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // Calendar state
  const [calendarData, setCalendarData] = useState([]);
  const [calendarLabel, setCalendarLabel] = useState('');
  const [calendarLoading, setCalendarLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [showMonthPicker, setShowMonthPicker] = useState(false);

  const fetchCalendar = useCallback(async (month = '') => {
    setCalendarLoading(true);
    try {
      const params = month ? { month } : {};
      const res = await axios.get('/reports/daily-calendar', { params });
      setCalendarData(res.data.days || []);
      setCalendarLabel(res.data.label || '');
    } catch { /* silent */ }
    setCalendarLoading(false);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = period === 'custom'
        ? { period: 'custom', start_date: customRange.start, end_date: customRange.end }
        : { period };
      const res = await axios.get('/reports/certificate-data', { params });
      setData(res.data);
    } catch {
      toast.error('Failed to load performance data');
    }
    setLoading(false);
  }, [period, customRange.start, customRange.end]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchCalendar(selectedMonth); }, [fetchCalendar, selectedMonth]);
  useAutoRefresh(fetchData);

  const allEmployees = data?.employees || [];
  const pageSummaries = data?.page_summaries || [];

  // Overall stats
  const totalDeposits = pageSummaries.reduce((s, p) => s + p.total_deposit, 0);
  const totalWithdrawals = pageSummaries.reduce((s, p) => s + p.total_withdraw, 0);
  const totalNet = pageSummaries.reduce((s, p) => s + p.net_collection, 0);
  const totalNewPlayers = pageSummaries.reduce((s, p) => s + p.new_players, 0);

  // Month picker options (last 12 months)
  const monthOptions = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    monthOptions.push({ value: val, label });
  }

  // Build calendar grid
  // Build calendar weeks correctly
  const buildWeeks = () => {
    if (calendarData.length === 0) return [];
    const weeks = [];
    const firstDate = new Date(calendarData[0].date + 'T12:00:00');
    const startDow = firstDate.getDay();
    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    calendarData.forEach(d => cells.push(d));
    while (cells.length % 7 !== 0) cells.push(null);
    for (let i = 0; i < cells.length; i += 7) {
      weeks.push(cells.slice(i, i + 7));
    }
    return weeks;
  };
  const weeks = buildWeeks();

  // Today's date string (local timezone)
  const todayStr = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();

  return (
    <div className="min-h-screen bg-[#080810]" data-testid="performance-reports-page">
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <MobileHeader onMenuClick={() => setSidebarOpen(true)} />

      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-purple-500/5 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-cyan-500/5 rounded-full blur-[150px]" />
      </div>

      <main className="lg:ml-72 min-h-screen relative z-10 p-4 md:p-6 lg:p-8">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 rounded-xl" style={{ background: 'linear-gradient(135deg, rgba(153,69,255,0.2), rgba(0,245,255,0.2))', border: '1px solid rgba(153,69,255,0.3)' }}>
              <BarChart3 className="w-8 h-8" style={{ color: THEME.neonPurple }} />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-black text-white">PERFORMANCE</h1>
              <p className="text-gray-400 text-sm">Employee Reports & Certificates</p>
            </div>
          </div>
          <PeriodSelector period={period} setPeriod={setPeriod} customRange={customRange} setCustomRange={setCustomRange} />
        </motion.div>

        {/* ===== CALENDAR SECTION ===== */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6" data-testid="calendar-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <Calendar className="w-5 h-5" style={{ color: THEME.neonCyan }} /> Daily Overview
              <span className="text-sm font-normal text-gray-400 ml-2">{calendarLabel}</span>
            </h2>
            <div className="relative">
              <button
                onClick={() => setShowMonthPicker(!showMonthPicker)}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all bg-[#0d0d15] text-gray-400 hover:text-white border border-white/5"
                data-testid="month-picker-btn"
              >
                <Calendar className="w-3.5 h-3.5" />
                {selectedMonth ? monthOptions.find(m => m.value === selectedMonth)?.label : 'Select Month'}
              </button>
              {showMonthPicker && (
                <div className="absolute right-0 top-full mt-1 w-56 rounded-xl overflow-hidden shadow-2xl z-30" style={{ background: '#12121f', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <button
                    onClick={() => { setSelectedMonth(''); setShowMonthPicker(false); }}
                    className={`w-full px-4 py-2.5 text-left text-sm transition-colors hover:bg-white/5 ${!selectedMonth ? 'text-[#00F5FF] font-bold' : 'text-gray-300'}`}
                    data-testid="month-option-rolling"
                  >Last 30 Days (Default)</button>
                  <ScrollArea className="max-h-60">
                    {monthOptions.map(m => (
                      <button key={m.value}
                        onClick={() => { setSelectedMonth(m.value); setShowMonthPicker(false); }}
                        className={`w-full px-4 py-2.5 text-left text-sm transition-colors hover:bg-white/5 ${selectedMonth === m.value ? 'text-[#00F5FF] font-bold' : 'text-gray-300'}`}
                        data-testid={`month-option-${m.value}`}
                      >{m.label}</button>
                    ))}
                  </ScrollArea>
                </div>
              )}
            </div>
          </div>

          {calendarLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 rounded-full border-3 border-cyan-500/30 border-t-cyan-500 animate-spin" />
            </div>
          ) : (
            <Card className="rounded-xl bg-[#0d0d15] border-0 overflow-hidden p-3" data-testid="calendar-grid">
              {/* Day headers */}
              <div className="grid grid-cols-7 gap-1 mb-1">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                  <div key={d} className="text-center text-[10px] font-bold text-gray-500 uppercase tracking-wider py-1">{d}</div>
                ))}
              </div>
              {/* Weeks */}
              {weeks.map((week, wi) => (
                <div key={wi} className="grid grid-cols-7 gap-1">
                  {week.map((cell, ci) => {
                    if (!cell) return <div key={ci} className="rounded-lg" style={{ minHeight: '70px' }} />;
                    const day = new Date(cell.date + 'T12:00:00').getDate();
                    const hasActivity = cell.total_deposit > 0 || cell.total_withdraw > 0;
                    const isToday = cell.date === todayStr;
                    return (
                      <div key={ci} className={`rounded-lg p-1.5 flex flex-col items-center justify-center transition-all ${isToday ? 'ring-1 ring-[#00F5FF]/50' : ''}`}
                        style={{ background: hasActivity ? 'rgba(0,245,255,0.04)' : 'rgba(255,255,255,0.01)', minHeight: '70px' }}
                        data-testid={`cal-day-${cell.date}`}
                      >
                        <span className={`text-xs font-bold mb-1 ${isToday ? 'text-[#00F5FF]' : 'text-gray-400'}`}>{day}</span>
                        <span className="text-[10px] font-bold" style={{ color: hasActivity ? THEME.neonGreen : '#444' }}>
                          {cell.total_deposit >= 1000 ? `$${(cell.total_deposit / 1000).toFixed(1)}k` : `$${Math.round(cell.total_deposit)}`}
                        </span>
                        <span className={`text-[10px] font-bold ${!hasActivity ? 'text-[#444]' : cell.total_net >= 0 ? 'text-[#FFD700]' : 'text-[#FF3366]'}`}>
                          {cell.total_net >= 0 ? '+' : ''}{cell.total_net >= 1000 || cell.total_net <= -1000 ? `$${(cell.total_net / 1000).toFixed(1)}k` : `$${Math.round(cell.total_net)}`}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ))}
              {weeks.length === 0 && (
                <div className="py-8 text-center text-gray-500 text-sm">No data for this period</div>
              )}
              {/* Legend */}
              <div className="flex items-center justify-center gap-4 mt-3 pt-2 border-t border-white/5">
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full" style={{ background: THEME.neonGreen }} />
                  <span className="text-[10px] text-gray-400">Deposit</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full" style={{ background: THEME.neonGold }} />
                  <span className="text-[10px] text-gray-400">Net (Remaining)</span>
                </div>
              </div>
            </Card>
          )}
        </motion.div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-12 h-12 rounded-full border-4 border-purple-500/30 border-t-purple-500 animate-spin" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Overall Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="overall-summary">
              <StatBox label="Total Deposits" value={formatCurrency(totalDeposits)} icon={TrendingUp} color={THEME.neonGreen} />
              <StatBox label="Total Withdrawals" value={formatCurrency(totalWithdrawals)} icon={TrendingDown} color={THEME.neonPink} />
              <StatBox label="Net Collection" value={formatCurrency(totalNet)} icon={DollarSign} color={THEME.neonGold} />
              <StatBox label="New Players" value={totalNewPlayers} icon={UserPlus} color={THEME.neonCyan} />
            </div>

            {/* Page Summaries */}
            {pageSummaries.length > 0 && (
              <div data-testid="page-summaries">
                <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                  <Layers className="w-5 h-5" style={{ color: THEME.neonPurple }} /> Page Performance
                </h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {pageSummaries.map((pg, idx) => (
                    <Card key={idx} className="p-4 rounded-xl bg-[#0d0d15] border-0">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-bold text-white">{pg.page_name}</h3>
                        <Badge className="text-[10px] bg-white/5 text-gray-300">{pg.active_employees} employees</Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div><p className="text-[10px] text-gray-500">Deposits</p><p className="font-bold" style={{ color: THEME.neonGreen }}>{formatCurrency(pg.total_deposit)}</p></div>
                        <div><p className="text-[10px] text-gray-500">Withdrawals</p><p className="font-bold" style={{ color: THEME.neonPink }}>{formatCurrency(pg.total_withdraw)}</p></div>
                        <div><p className="text-[10px] text-gray-500">Net</p><p className="font-bold" style={{ color: THEME.neonGold }}>{formatCurrency(pg.net_collection)}</p></div>
                        <div><p className="text-[10px] text-gray-500">New Players</p><p className="font-bold" style={{ color: THEME.neonCyan }}>{pg.new_players}</p></div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Employee Rankings */}
            <div data-testid="employee-rankings">
              <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                <Trophy className="w-5 h-5" style={{ color: THEME.neonGold }} /> Employee Rankings
              </h2>
              <Card className="rounded-xl bg-[#0d0d15] border-0 overflow-hidden">
                <div className="divide-y divide-white/5">
                  {allEmployees.map((emp) => {
                    const gradeColor = GRADE_COLORS[emp.grade] || '#999';
                    const cur = emp.current;
                    return (
                      <motion.button key={emp.employee_id} whileHover={{ backgroundColor: 'rgba(255,255,255,0.03)' }}
                        onClick={() => setSelectedEmployee(emp)}
                        className="w-full p-4 flex items-center gap-4 text-left transition-colors"
                        data-testid={`employee-row-${emp.employee_id}`}>
                        {/* Rank */}
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center font-black shrink-0"
                          style={{
                            background: emp.rank === 1 ? THEME.neonGold : emp.rank === 2 ? '#C0C0C0' : emp.rank === 3 ? '#CD7F32' : 'rgba(255,255,255,0.08)',
                            color: emp.rank <= 3 ? '#000' : '#888'
                          }}>
                          {emp.rank <= 3 ? <Crown className="w-5 h-5" /> : emp.rank}
                        </div>

                        {/* Name & page */}
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-white truncate">{emp.employee_name}</p>
                          <p className="text-xs text-gray-500">{emp.page_name}</p>
                        </div>

                        {/* Grade */}
                        <Badge className="text-xs font-black shrink-0" style={{ background: `${gradeColor}20`, color: gradeColor, border: `1px solid ${gradeColor}40` }}>
                          {emp.grade}
                        </Badge>

                        {/* Score */}
                        <div className="text-right shrink-0">
                          <p className="font-mono font-bold text-white">{cur.score}</p>
                          <p className="text-[10px] text-gray-500">score</p>
                        </div>

                        {/* Deposit volume */}
                        <div className="text-right shrink-0 hidden md:block">
                          <p className="font-mono font-bold" style={{ color: THEME.neonGreen }}>{formatCurrency(cur.total_deposit)}</p>
                          <p className="text-[10px] text-gray-500">deposits</p>
                        </div>

                        <ChevronRight className="w-4 h-4 text-gray-600 shrink-0" />
                      </motion.button>
                    );
                  })}
                  {allEmployees.length === 0 && (
                    <div className="p-12 text-center text-gray-500">No employee data for this period</div>
                  )}
                </div>
              </Card>
            </div>

            {/* Period label */}
            {data && (
              <p className="text-center text-xs text-gray-600 mt-4">Showing data for: {data.period_label}</p>
            )}
          </div>
        )}
      </main>

      {/* Certificate Modal */}
      <AnimatePresence>
        {selectedEmployee && (
          <PerformanceCertificate
            employee={selectedEmployee}
            periodLabel={data?.period_label || ''}
            onClose={() => setSelectedEmployee(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
